'use client';

import { memo, useCallback, useEffect } from 'react';

import { Spinner } from '@air/primitive-spinner';

import { getTreeItemPaddingLeft } from '~/components/tree/TreeItem';
import { useIsInView } from '~/hooks/useIsInView';
import { type TreePagination } from '~/hooks/useTreePagination';

export type TreeLoadMoreRowProps = {
  depth: number;
  pagination: TreePagination;
};

/**
 * Ends a paged tree level and loads its next page once it scrolls into view, so a level longer than one page is
 * complete by the time the user reaches the end of it. It relies on sitting inside the sidebar's bounded
 * scroller: anywhere it is always on screen, it would fetch every page back to back.
 */
export const TreeLoadMoreRow = memo(({ depth, pagination }: TreeLoadMoreRowProps) => {
  const { fetchNextPage, hasNextPage, isFetchingNextPage, isFetchNextPageError } = pagination;
  const { isInView, ref } = useIsInView();

  // A failed page stops the automatic load. Retrying each time the request settles would loop for as long as the
  // row stays on screen, so the user retries instead.
  const shouldLoadNextPage = isInView && hasNextPage && !isFetchingNextPage && !isFetchNextPageError;

  useEffect(() => {
    if (shouldLoadNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, shouldLoadNextPage]);

  const handleRetry = useCallback(() => {
    if (isFetchingNextPage) {
      return;
    }

    fetchNextPage();
  }, [fetchNextPage, isFetchingNextPage]);

  if (!hasNextPage) {
    return null;
  }

  const paddingLeft = getTreeItemPaddingLeft(depth);

  // The query stays in its next-page error state until a retry succeeds, so the button stays mounted while the retry
  // is in flight and keyboard focus stays on it. `aria-disabled` rather than `disabled`, which is not focusable.
  if (isFetchNextPageError) {
    return (
      <button
        aria-busy={isFetchingNextPage}
        aria-disabled={isFetchingNextPage}
        className="flex h-9 w-full items-center rounded-6 font-medium text-12 text-neutral-10 hover:bg-neutral-3 hover:text-neutral-12"
        onClick={handleRetry}
        style={{ paddingLeft }}
        type="button"
      >
        {isFetchingNextPage ? <Spinner className="size-4 text-neutral-9" /> : 'Couldn’t load more. Try again'}
      </button>
    );
  }

  return (
    <div className="flex h-9 items-center" ref={ref} style={{ paddingLeft }}>
      <Spinner className="size-4 text-neutral-9" />
    </div>
  );
});

TreeLoadMoreRow.displayName = 'TreeLoadMoreRow';
